import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, Loader2 } from "lucide-react";
import { Toaster, toast } from "sonner";

const EVENT_NAME = "maajja:join";

export const openJoinModal = () => {
    window.dispatchEvent(new CustomEvent(EVENT_NAME));
};

const initialForm = {
    name: "",
    email: "",
    phone: "",
    social: "",
};

export const JoinModal = () => {
    const [open, setOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState(initialForm);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        const handler = () => setOpen(true);
        window.addEventListener(EVENT_NAME, handler);
        return () => window.removeEventListener(EVENT_NAME, handler);
    }, []);

    useEffect(() => {
        if (!open) return;
        const onEsc = (e) => e.key === "Escape" && setOpen(false);
        document.body.style.overflow = "hidden";
        window.addEventListener("keydown", onEsc);
        return () => {
            document.body.style.overflow = "";
            window.removeEventListener("keydown", onEsc);
        };
    }, [open]);

    const update = (k) => (e) => {
        setForm((f) => ({ ...f, [k]: e.target.value }));
        setErrors((er) => ({ ...er, [k]: undefined }));
    };

    const validate = () => {
        const e = {};
        if (!form.name.trim() || form.name.trim().length < 2)
            e.name = "Please enter your full name";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
            e.email = "Please enter a valid email";
        if (!/^[+()\-\s\d]{7,}$/.test(form.phone))
            e.phone = "Please enter a valid phone number";
        if (!form.social.trim())
            e.social = "Add your handle (e.g. @yourname)";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = async (ev) => {
        ev.preventDefault();
        if (!validate()) return;
        setSubmitting(true);
        // Persist to localStorage so signups aren't lost client-side
        try {
            const list = JSON.parse(
                localStorage.getItem("maajja_waitlist") || "[]",
            );
            list.push({ ...form, ts: new Date().toISOString() });
            localStorage.setItem("maajja_waitlist", JSON.stringify(list));
        } catch {
            /* ignore */
        }
        await new Promise((r) => setTimeout(r, 800));
        setSubmitting(false);
        toast.success("You're on the list!", {
            description: `Welcome to Maajja, ${form.name.split(" ")[0]}. We'll be in touch.`,
        });
        setForm(initialForm);
        setOpen(false);
    };

    return (
        <>
            <Toaster
                theme="dark"
                position="top-center"
                toastOptions={{
                    style: {
                        background: "#0a0436",
                        border: "1px solid rgba(121,30,226,0.4)",
                        color: "#fff",
                    },
                }}
            />
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6"
                        data-testid="join-modal-overlay"
                    >
                        {/* Backdrop */}
                        <div
                            className="absolute inset-0 bg-[#030023]/85 backdrop-blur-md"
                            onClick={() => !submitting && setOpen(false)}
                        />

                        {/* Modal */}
                        <motion.div
                            initial={{ opacity: 0, y: 30, scale: 0.96 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 20, scale: 0.97 }}
                            transition={{ duration: 0.35, ease: "easeOut" }}
                            className="relative w-full max-w-lg rounded-3xl overflow-hidden border border-white/10"
                            style={{
                                background:
                                    "radial-gradient(ellipse at 20% 0%, rgba(121,30,226,0.35) 0%, transparent 55%), radial-gradient(ellipse at 80% 100%, rgba(245,72,21,0.22) 0%, transparent 55%), #0a0436",
                            }}
                            data-testid="join-modal"
                        >
                            {/* Close */}
                            <button
                                onClick={() => !submitting && setOpen(false)}
                                className="absolute top-5 right-5 w-9 h-9 rounded-full border border-white/15 hover:border-white/40 hover:bg-white/5 flex items-center justify-center text-white/70 hover:text-white transition z-10"
                                aria-label="Close"
                                data-testid="join-modal-close"
                            >
                                <X size={16} />
                            </button>

                            <div className="p-8 sm:p-10">
                                {/* Header */}
                                <div className="mb-7">
                                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/15 bg-white/5 mb-5">
                                        <span className="w-1.5 h-1.5 rounded-full bg-[#F54815] animate-pulse" />
                                        <span className="text-[10px] uppercase tracking-[0.25em] text-white/80 font-semibold">
                                            Free to join
                                        </span>
                                    </div>
                                    <h2
                                        className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tighter text-white leading-[1.05] mb-3"
                                        data-testid="join-modal-title"
                                    >
                                        Join <span className="gradient-text">Maajja</span>.
                                    </h2>
                                    <p className="text-white/60 text-sm leading-relaxed">
                                        Tell us a bit about yourself. We'll get you
                                        set up with early access and onboarding.
                                    </p>
                                </div>

                                <form onSubmit={handleSubmit} className="space-y-4" data-testid="join-form">
                                    <Field
                                        label="Full name"
                                        placeholder="e.g. A. R. Rahman"
                                        value={form.name}
                                        onChange={update("name")}
                                        error={errors.name}
                                        testId="join-name"
                                        autoFocus
                                    />
                                    <Field
                                        label="Email address"
                                        type="email"
                                        placeholder="you@example.com"
                                        value={form.email}
                                        onChange={update("email")}
                                        error={errors.email}
                                        testId="join-email"
                                    />
                                    <Field
                                        label="Phone number"
                                        type="tel"
                                        placeholder="+1 555 000 0000"
                                        value={form.phone}
                                        onChange={update("phone")}
                                        error={errors.phone}
                                        testId="join-phone"
                                    />
                                    <Field
                                        label="Social media handle"
                                        placeholder="@yourname (instagram, x, tiktok…)"
                                        value={form.social}
                                        onChange={update("social")}
                                        error={errors.social}
                                        testId="join-social"
                                    />

                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className="group w-full mt-3 inline-flex items-center justify-center gap-2 bg-[#791EE2] hover:bg-[#8b32f0] disabled:opacity-70 disabled:cursor-not-allowed text-white px-6 py-4 rounded-full font-semibold transition-all hover:shadow-[0_0_40px_rgba(121,30,226,0.55)] active:scale-[0.98]"
                                        data-testid="join-submit"
                                    >
                                        {submitting ? (
                                            <>
                                                <Loader2
                                                    size={18}
                                                    className="animate-spin"
                                                />
                                                Joining…
                                            </>
                                        ) : (
                                            <>
                                                Join now
                                                <Check
                                                    size={16}
                                                    className="opacity-90 group-hover:translate-x-0.5 transition-transform"
                                                />
                                            </>
                                        )}
                                    </button>

                                    <p className="text-center text-[11px] text-white/40 pt-2">
                                        By joining, you agree to our terms &
                                        privacy policy.
                                    </p>
                                </form>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

const Field = ({ label, error, testId, ...rest }) => (
    <label className="block">
        <span className="block text-[11px] uppercase tracking-[0.18em] text-white/55 font-medium mb-2">
            {label}
        </span>
        <input
            {...rest}
            data-testid={testId}
            className={`w-full bg-white/[0.04] border ${
                error ? "border-[#F54815]/60" : "border-white/10"
            } focus:border-[#791EE2] focus:bg-white/[0.06] outline-none rounded-xl px-4 py-3.5 text-white placeholder:text-white/30 transition-all text-[15px]`}
        />
        {error && (
            <span
                className="text-xs text-[#F54815] mt-1.5 block"
                data-testid={`${testId}-error`}
            >
                {error}
            </span>
        )}
    </label>
);
